import { z } from 'zod';
import { AdPlacementSchema } from './api';
import { QuestionViewDataSchema } from './quiz';
import { UserProfileViewDataSchema } from './user';

// Social features — follows, feeds, and user suggestions. Schemas are
// authoritative; TS types are derived via z.infer<>.

/** User data for follow relationships */
export const UserFollowDataSchema = z.object({
  userId: z.string(),
  displayName: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  photoURL: z.string().optional(),
  professionalTitle: z.string().optional(),
  medicalSpecialty: z.string().optional(),
  followedAt: z.string(), // ISO string timestamp
});
export type UserFollowData = z.infer<typeof UserFollowDataSchema>;

/** Follow statistics for a user */
export const FollowStatsSchema = z.object({
  followersCount: z.number(),
  followingCount: z.number(),
});
export type FollowStats = z.infer<typeof FollowStatsSchema>;

/** Suggested user for recommendations */
export const SuggestedUserSchema = z.object({
  userId: z.string(),
  displayName: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  professionalTitle: z.string().optional(),
  medicalSpecialty: z.string().optional(),
  location: z.string().optional(),
  trainingProgram: z.string().optional(),
  photoURL: z.string().optional(),
  followersCount: z.number().optional(),
  reason: z.string().optional(),
  distance: z.number().optional(),
  distanceDescription: z.string().optional(),
});
export type SuggestedUser = z.infer<typeof SuggestedUserSchema>;

/**
 * Feed item representing user activity. Supports both the mobile activity feed
 * and frontend question views.
 */
export const FeedItemSchema = z.object({
  id: z.string().optional(), // Mobile format
  questionId: z.string().optional(), // Frontend format - primary question reference
  userId: z.string(),
  userName: z.string().optional(), // Mobile format
  displayName: z.string().optional(), // Frontend format
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  userPhotoURL: z.string().optional(), // Mobile format
  photoURL: z.string().optional(), // Frontend format
  action: z.string().optional(), // Mobile: "completed_quiz", "followed_user"
  questionText: z.string().optional(), // Frontend: actual question content
  subject: z.string().optional(),
  quizId: z.string().optional(),
  quizTitle: z.string().optional(),
  timestamp: z.string().optional(), // Mobile: ISO string
  createdAt: z.string().optional(), // Mobile: alias for timestamp
  viewedAt: z.string().optional(), // Frontend: when question was viewed
  viewedBy: z
    .object({
      userId: z.string(),
      displayName: z.string().optional(),
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      photoURL: z.string().optional(),
    })
    .optional(),
  user: UserProfileViewDataSchema.optional(),
  question: z.any().optional(),
});
export type FeedItem = z.infer<typeof FeedItemSchema>;

/** Question used in the tailored feed with optional comment preview data */
export const QuestionWithCommentPreviewSchema = QuestionViewDataSchema.extend({
  // Optional ONLY on the feed. QuestionViewData requires it and the quiz path genuinely sends it
  // (sanitizeQuestionForQuiz keeps it), but the feed's sanitizeQuestion strips it — a backend test
  // pins its absence. The schema claimed it was always present, so clients typed it as `string` and
  // passed it on believing it was there; it is in fact never sent on this path, which is why the
  // explanation modal has to re-fetch it by id.
  explanation: z.string().optional(),
  commentCount: z.number().optional(),
  type: z.literal('question').optional(),
});
export type QuestionWithCommentPreview = z.infer<typeof QuestionWithCommentPreviewSchema>;

/** Comment item that appears in the tailored feed */
export const CommentFeedItemSchema = z.object({
  type: z.literal('comment'),
  id: z.string(),
  questionId: z.string(),
  questionText: z.string(),
  // Matches what the backend actually emits (TailoredFeedComment.questionSpecialty). It was
  // declared as `questionSubject` here while the backend sent `questionSpecialty`, and because
  // the field is optional nothing type-checked the mismatch — so the pill never rendered.
  questionSpecialty: z.string().optional(),
  commentText: z.string(),
  // commenter and repliesCount are REQUIRED because the backend's single construction site always
  // sets both (Question.ts, the comment feed item). They were optional here purely because this
  // schema was hand-maintained alongside a separate backend interface rather than derived from it.
  commenter: z.object({
    userId: z.string(),
    displayName: z.string().nullable().optional(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    photoURL: z.string().nullable().optional(),
  }),
  repliesCount: z.number(),
  commentedAt: z.string().optional(),
  questionCommentCount: z.number().optional(),
});
export type CommentFeedItem = z.infer<typeof CommentFeedItemSchema>;

/** Sponsored content that can appear inline in the feed */
export const FeedAdItemSchema = z.object({
  type: z.literal('ad'),
  id: z.string(),
  campaignId: z.string().optional(),
  title: z.string(),
  // Optional because AdDocument.text is optional — the schema claimed it was always present.
  text: z.string().optional(),
  imageUrl: z.string().optional(),
  targetUrl: z.string().optional(),
  // Always present on the wire, and nullable: the serve path normalises a missing CTA to null so
  // clients have one shape to handle. The backend interface said `string | undefined`, which the
  // wire never actually carried.
  ctaText: z.string().nullable(),
  // The real types, not `string`: placement is the canonical AdPlacement enum and the label is a
  // constant. Both are always set at the single construction site.
  label: z.literal('Sponsored'),
  placement: AdPlacementSchema,
  priority: z.number().optional(),
  /**
   * Proof-of-serve token returned on POST /ads/impression so the server bills only impressions for
   * ads it actually served (see backend services/adProofToken). Present only when proof-of-serve
   * is configured; opaque to the client.
   */
  token: z.string().optional(),
});
export type FeedAdItem = z.infer<typeof FeedAdItemSchema>;

/** Items that can appear in the tailored feed */
export const TailoredFeedItemSchema = z.union([
  QuestionWithCommentPreviewSchema,
  CommentFeedItemSchema,
  FeedAdItemSchema,
]);
export type TailoredFeedItem = z.infer<typeof TailoredFeedItemSchema>;

/** Paginated tailored feed response from the backend */
export const TailoredQuestionsResponseSchema = z.object({
  feed: z.array(TailoredFeedItemSchema),
  totalPool: z.number(),
  limit: z.number(),
  page: z.number(),
  pagesPerCycle: z.number(),
  pageInCycle: z.number(),
  cycle: z.number(),
  looped: z.boolean(),
  hasMoreInCycle: z.boolean(),
  // Always present: both return paths in getTailoredQuestions set it (page + 1, or page when the
  // pool is empty). It was optional here only because this schema was maintained by hand.
  nextPage: z.number(),
});
export type TailoredQuestionsResponse = z.infer<typeof TailoredQuestionsResponseSchema>;
